import React from 'react';
import Button from '../../../../shared/components/Button/Button';
import Card from '../../../../shared/components/Card/Card';
import Chip from '../../../../shared/components/Chip/Chip';
import Drawer from '../../../../shared/components/Drawer/Drawer';
import EmptyState from '../../../../shared/components/EmptyState/EmptyState';
import SearchInput from '../../../../shared/components/SearchInput/SearchInput';
import Select from '../../../../shared/components/Select/Select';
import Typography from '../../../../shared/components/Typography/Typography';
import { formatWikiDate } from '../wiki.date';
import useMediaQuery from '../../../../hooks/useMediaQuery';
import { getWikiDocumentVisual } from './WikiIcons';

const statusVariant = {
  Publie: 'success',
  Brouillon: 'neutral',
  'A valider': 'warning',
  'A reviser': 'danger',
  Archive: 'neutral',
};

const highlightText = (value = '', query = '') => {
  const text = String(value || '');
  const needle = query.trim();
  if (!needle) {
    return text;
  }

  const index = text.toLowerCase().indexOf(needle.toLowerCase());
  if (index === -1) {
    return text;
  }

  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + needle.length)}</mark>
      {text.slice(index + needle.length)}
    </>
  );
};

const normalizeSearchValue = (value = '') => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const getSearchTerms = (query = '') => normalizeSearchValue(query)
  .split(/\s+/)
  .map((term) => term.trim())
  .filter(Boolean);

const scoreField = (value, terms, weight) => {
  const normalized = normalizeSearchValue(value);
  if (!normalized || !terms.length) {
    return 0;
  }

  return terms.reduce((score, term) => (
    normalized.includes(term) ? score + weight : score
  ), 0);
};

const getArticleRelevanceScore = (article, query, getCategoryLabel) => {
  const terms = getSearchTerms(query);
  if (!terms.length) {
    return 0;
  }

  return [
    scoreField(article.title, terms, 8),
    scoreField(article.type, terms, 5),
    scoreField(getCategoryLabel(article.category), terms, 5),
    scoreField(article.tags, terms, 4),
    scoreField(article.summary, terms, 3),
    scoreField(article.owner, terms, 2),
    scoreField(article.status, terms, 2),
    scoreField(article.plainText || article.content || article.renderedHtml, terms, 1),
  ].reduce((total, score) => total + score, 0);
};

function WikiSearchPage({
  getCategoryLabel,
  initialStatusFilter = '',
  mode,
  onOpenArticle,
  onSearchChange,
  routeValue,
  rows,
  search,
}) {
  const [typeFilter, setTypeFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState(initialStatusFilter);
  const [sortMode, setSortMode] = React.useState('relevance');
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const compactFilters = useMediaQuery('(max-width: 560px)');
  const filtersDrawerTitleId = React.useId();
  React.useEffect(() => setStatusFilter(initialStatusFilter), [initialStatusFilter]);
  const typeOptions = React.useMemo(() => (
    [...new Set(rows.map((article) => article.type).filter(Boolean))]
      .sort()
      .map((type) => ({ value: type, label: type }))
  ), [rows]);
  const statusOptions = React.useMemo(() => (
    [...new Set(rows.map((article) => article.status).filter(Boolean))]
      .sort()
      .map((status) => ({ value: status, label: status }))
  ), [rows]);
  const filteredRows = React.useMemo(() => {
    const filtered = rows.filter((article) => (
      (!typeFilter || article.type === typeFilter)
      && (!statusFilter || article.status === statusFilter)
    ));

    return [...filtered].sort((a, b) => {
      if (sortMode === 'relevance') {
        const scoreDiff = getArticleRelevanceScore(b, search, getCategoryLabel)
          - getArticleRelevanceScore(a, search, getCategoryLabel);

        if (scoreDiff !== 0) {
          return scoreDiff;
        }

        return String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''));
      }

      if (sortMode === 'title') {
        return String(a.title || '').localeCompare(String(b.title || ''));
      }
      return String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''));
    });
  }, [getCategoryLabel, rows, search, sortMode, statusFilter, typeFilter]);
  const title = mode === 'category' ? getCategoryLabel(routeValue) : 'Recherche documentaire';
  const renderFilters = (className = '') => (
    <div className={['wiki-search-filters', className].filter(Boolean).join(' ')}>
      <Select
        fullWidth
        label="Type"
        placeholder="Tous"
        value={typeFilter}
        onChange={(event) => setTypeFilter(event.target.value)}
        options={typeOptions}
      />
      <Select
        fullWidth
        label="Statut"
        placeholder="Tous"
        value={statusFilter}
        onChange={(event) => setStatusFilter(event.target.value)}
        options={statusOptions}
      />
      <Select
        fullWidth
        label="Tri"
        value={sortMode}
        onChange={(event) => setSortMode(event.target.value)}
        options={[
          { value: 'relevance', label: 'Pertinence' },
          { value: 'date', label: 'Date de mise à jour' },
          { value: 'title', label: 'Titre' },
        ]}
      />
    </div>
  );

  return (
    <div className="wiki-content-stack">
      <section className="wiki-search-hero">
        <header className="wiki-section-header wiki-search-heading">
          <div>
            <Typography variant="h4">{title}</Typography>
            <p>Trouvez rapidement les informations dont vous avez besoin.</p>
          </div>
        </header>
        <SearchInput
          actionLabel="Rechercher"
          fullWidth
          label="Rechercher"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onSubmit={() => {}}
          placeholder="DOE, procedure, referent, tag..."
        />
      </section>
      <div className="wiki-search-panel">
        {compactFilters ? (
          <Button className="wiki-search-filter-button" variant="secondary" onClick={() => setFiltersOpen(true)}>
            Filtres
          </Button>
        ) : renderFilters('wiki-search-filters--desktop')}
      </div>
      <div className="wiki-search-results-toolbar">
        <span className="wiki-search-count">{filteredRows.length} résultat(s)</span>
      </div>
      <div className="wiki-search-results">
        {filteredRows.map((article) => {
          const { Icon: DocumentIcon, tone } = getWikiDocumentVisual(article.type);
          return (
          <Card key={article.id} className="wiki-search-result" interactive onClick={() => onOpenArticle(article)}>
            <div className="wiki-search-result-header">
              <span className={`wiki-search-result__icon wiki-icon-tone--${tone}`} aria-hidden="true"><DocumentIcon fontSize="small" /></span>
              <Chip size="small" variant="info">{article.type}</Chip>
              <Chip size="small" variant={statusVariant[article.status] || 'neutral'}>{article.status}</Chip>
            </div>
            <div className="wiki-search-result__copy">
              <strong className="wiki-search-result__title">{highlightText(article.title, search)}</strong>
              <p>{highlightText(article.summary || article.plainText || 'Aucun resume disponible.', search)}</p>
            </div>
            <dl className="wiki-search-result-meta">
              <div><dt>Categorie</dt><dd>{getCategoryLabel(article.category)}</dd></div>
              <div><dt>Referent</dt><dd>{article.owner || 'Non renseigne'}</dd></div>
              <div><dt>Mise a jour</dt><dd>{formatWikiDate(article.updatedAt)}</dd></div>
            </dl>
          </Card>
          );
        })}
        {!filteredRows.length && (
          <EmptyState title="Aucun resultat">
            Essayez un autre mot-clé ou retirez un filtre.
          </EmptyState>
        )}
      </div>
      <Drawer
        labelledBy={filtersDrawerTitleId}
        open={filtersOpen}
        placement="right"
        title="Filtres de recherche"
        onClose={() => setFiltersOpen(false)}
      >
        {renderFilters('wiki-search-filters--drawer')}
      </Drawer>
    </div>
  );
}

export default WikiSearchPage;
